import { type ProductPricingRangeEntity } from "../entities";

export type CreateProductRequestData = {
  category: string;
  productDetails: string;
};

export type CalculateCPARequestData = {
  productId: string;
  price: number;
  netDeliveryRate: number;
};

export type UpdateLandingPageSettingsData = {
  productPicturesURLs?: string[];
  price: number;
  isAddressFormLong: boolean;
  description?: string;
  active: boolean;
  url?: string;
  productName: string;
  id: string;
  pixelId?: string;
  country: string;
};

export enum HighlightGroups {
  NEW_ARRIVALS = "newArrivals",
  LOCKED_PRODUCTS = "lockedProducts",
  OFFERS = "offers",
  BEST_SELLERS = "bestSellers",
  OPEN_FOR_TESTING = "openForTesting",
  DISCOUNT_ON_QUANTITIES = "multiQuantityDiscount",
}

export type VariantFinancials = {
  price: number;
  profit: number;
  originalPrice: number;
  favouritePrice?: number;
  isFavouritePriceOutOfSync?: boolean;
  discount?: {
    price?: number;
    startsAt?: Date;
    expiresAt?: Date;
    type?: DiscountType;
    originalPrice?: number;
  };
  finalPrice: number;
  discountedPrice?: number;
  discountedProfit?: number;
  tieredPricing?: {
    quantity: number;
    price: number;
    profit: number;
    dynamicPrice?: number;
    dynamicProfit?: number;
  }[];
  pricingRange: ProductPricingRangeEntity;
};

export type BaseCategoryGroupsParams = {
  pageSize: number;
};

export type ListHighlightedVariantsParams = BaseCategoryGroupsParams & {
  highlightGroups: HighlightGroups[];
};

export type ListCategoryGroupedVariantsParams = BaseCategoryGroupsParams & {
  categoryIds: number[];
};

export type OrderTestingInfo = {
  maxQuantity: number;
  merchantsLeft: number;
  totalMerchants: number;
  succeededTestingLockRate: number;
};

export enum DiscountType {
  SALE = "sale",
  SECOND_PLACEMENT = "second_placement",
  ADDITIONAL_QUANTITY = "custom_additional_qty",
}

export type StockAvailability = {
  stockRange: StockRange;
  detailedStockRange: DetailedStockRange;
};

export enum StockRange {
  NOT_AVAILABLE = "not_available",
  BELOW_200 = "below_200",
  BETWEEN_200_AND_1000 = "between_200_and_1000",
  ABOVE_1000 = "above_1000",
}

export enum DetailedStockRange {
  NOT_AVAILABLE = "not_available",
  LESS_THAN_50 = "less_than_50",
  FROM_50_TO_100 = "from_50_to_100",
  FROM_100_TO_200 = "from_100_to_200",
  FROM_200_TO_400 = "from_200_to_400",
  FROM_400_TO_600 = "from_400_to_600",
  FROM_600_TO_800 = "from_600_to_800",
  FROM_800_TO_1000 = "from_800_to_1000",
  MORE_THAN_1000 = "more_than_1000",
}

export type LockInfo = {
  isLocked: boolean;
  quantity?: number;
  quantityUsed?: number;
  isOpenForTesting?: boolean;
  isLockingAvailable?: boolean;
  orderTestingStarted?: boolean;
  expiresAt?: Date;
  isExpired?: boolean;
};

export type ProductsSearchParams = {
  query?: string;
  page?: number;
  pageSize?: number;
  sortBy?: SortBy;
  categoryId?: number;
  isSale?: boolean;
  isDiscountedAsSecondProduct?: boolean;
  sortOrder?: SortOrder;
};

export enum SortOrder {
  ASC = "ascending",
  DESC = "descending",
}

export enum SortBy {
  SORT_BY_CLOSEST_MATCH = "closestMatch",
  SORT_BY_INTRODUCED_AT = "introducedAt",
  SORT_BY_UPDATED_AT = "updatedAt",
  SORT_BY_PRODUCT_PRICE = "price",
  SORT_BY_PRODUCT_PROFIT = "profit",
  SORT_BY_MERCHANT_MATCHING_SCORE = "merchantMatchingScore",
}

export type ListProductVariantsParams = ProductsSearchParams & {
  highlightGroup?: HighlightGroups;
  preorderType?: PreorderType;
  isFavourite?: boolean;
};

export enum PreorderType {
  INTERNAL = "internal",
  EXTERNAL = "external",
}
